<template>
  <div
    v-if="shouldRender"
    ref="pullHook"
    :class="[
      { '--ios': isIos },
      'pull-to-refresh d-flex align-items-center justify-content-center'
    ]"
    :style="{ transform: `translateY(${translateY}px)` }"
  >
    <div :style="{ opacity: opacity }">
      <template v-if="isIos">
        <template v-if="isRefreshing">
          Refreshing...
        </template>
        <template v-else-if="shouldRefresh">
          Release to Refresh
        </template>
        <template v-else>
          Pull To Refresh
        </template>
      </template>
      <template v-else>
        <div
          class="pull-to-refresh__wrapper"
          :style="{
            transform: `scale(${pullProgress})`
          }"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" :class="[{ '--refreshing': isRefreshing }]" :style="{
              transform: `rotate(${360 * pullProgress}deg)`
            }" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><g fill="none" fill-rule="evenodd"><path d="M0 0h24v24H0z"/><path d="M17.65 6.35A7.958 7.958 0 0012 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0112 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" fill="#2B78FE" fill-rule="nonzero"/></g></svg>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import throttle from "lodash/throttle"

import EventBus, { EVENTS } from "@/lib/event-bus"
import {
  isIos,
  isAppStore,
  isStandalone,
  isPreviewContext
} from "@/lib/is-platform"
import { eventPath } from "@/lib/utils"
import { getScrollRootElement } from "@/lib/page-utils"

const getTouchCoordinatesFrom = event => {
  return {
    x: event.targetTouches[0].screenX,
    y: event.targetTouches[0].screenY
  }
}

const REFERSH_OFFSET = 90
const MAX_OFFSET = 140
const PULL_TO_REFRESH_HEIGHT = 100

export default {
  data() {
    return {
      offsetTop: 0,
      opacity: 0,
      isIos: isIos(),
      isRefreshing: false,
      touchDown: false,
      touchStartY: 0
    }
  },

  methods: {
    setOffset(offset) {
      if (this.touchDown) {
        this.offsetTop = Math.min(offset, MAX_OFFSET)
        this.opacity =
          this.offsetTop > REFERSH_OFFSET ? 1 : this.offsetTop / REFERSH_OFFSET
      }
    },

    resetOffset() {
      if (this.isRefreshing) {
        this.offsetTop = REFERSH_OFFSET
      } else {
        this.offsetTop = 0
      }
    },

    refresh() {
      EventBus.$emit(EVENTS.SHOW_GLOBAL_LOADER)

      this.isRefreshing = true
      if (!this.isIos) {
        this.offsetTop = REFERSH_OFFSET
      }
      window.location.reload()
    }
  },

  mounted() {
    if (!this.shouldRender) return

    const pageEl = getScrollRootElement()

    const handleIosEvent = () => {
      if (document.querySelector("#mkLightboxContainer")) {
        // Don't start a refresh from a lightbox
        return
      }
      this.setOffset(
        -(window.pageYOffset || document.documentElement.scrollTop)
      )
    }

    const handleAndroidEvent = e => {
      const coords = getTouchCoordinatesFrom(e)
      const yMovement = (this.touchStartY - coords.y) * 0.5

      if (pageEl.scrollTop <= 0 && yMovement) {
        this.setOffset(Math.abs(yMovement))
      }
    }

    pageEl.body.style.marginTop = `-${PULL_TO_REFRESH_HEIGHT}px`

    pageEl.addEventListener("touchstart", e => {
      if (
        eventPath(e).find(
          node => node.dataset && node.dataset.role === "message-thread"
        )
      ) {
        // Don't start a refresh from an inner scroll
        return
      }

      this.isRefreshing = false
      this.resetOffset()
      this.touchDown = true
    })
    pageEl.addEventListener("touchend", () => {
      this.touchDown = false

      if (this.shouldRefresh) {
        this.refresh()
      }

      this.resetOffset()
      setTimeout(() => {
        // Ensure offset is reset for inertia scrolling
        this.resetOffset()
      }, 500)
    })

    if (this.isIos) {
      pageEl.addEventListener("scroll", throttle(handleIosEvent, 80))
    } else {
      pageEl.addEventListener("touchstart", e => {
        this.touchStartY = getTouchCoordinatesFrom(e).y
      })

      pageEl.addEventListener("touchmove", throttle(handleAndroidEvent, 80))

      pageEl.addEventListener("touchend", () => {
        this.touchStartY = 0
      })
    }
  },

  computed: {
    shouldRender() {
      return !isPreviewContext() && (isAppStore() || isStandalone())
    },

    translateY() {
      if (this.isIos) {
        return false // Vue removes style attr
      } else if (this.isRefreshing) {
        return REFERSH_OFFSET
      } else {
        return this.offsetTop
      }
    },

    pullProgress() {
      return this.offsetTop > REFERSH_OFFSET
        ? 1
        : this.offsetTop / REFERSH_OFFSET
    },

    shouldRefresh() {
      return this.offsetTop > REFERSH_OFFSET
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

.pull-to-refresh {
  height: 100px;
  position: relative;
  z-index: 100;
  transition: transform 0.16s ease;

  &.--ios {
    background: $color-ui--white;
  }
}

.pull-to-refresh__wrapper {
  background: $color-ui--white;
  display: flex;
  align-items: center;
  justify-content: center;
  height: $size--9;
  width: $size--9;
  border-radius: $size--9;
  box-shadow: $box-shadow--medium;
  transition: transform 0.2s ease;

  svg {
    height: $size--8;
    width: $size--8;
    transition: transform 0.2s ease;

    path:last-child {
      fill: var(--brand-color-hex);
    }

    &.--refreshing {
      animation: spin 0.92s ease infinite;
    }
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>
